import React from 'react';
import { useTranslation } from 'react-i18next';

export const BuyPage: React.FC = () => {
  const { t } = useTranslation();

  const buyingSteps = [
    {
      step: 1,
      icon: 'fas fa-user-plus',
      duration: '5-10 min'
    },
    {
      step: 2,
      icon: 'fas fa-wallet',
      duration: '2-5 min'
    },
    {
      step: 3,
      icon: 'fas fa-search',
      duration: '1 min'
    },
    {
      step: 4,
      icon: 'fas fa-shopping-cart',
      duration: 'Instant'
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="hero-background absolute inset-0">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='rgba(212, 175, 55, 0.1)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <div className="hero-badge inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('buy.title')}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight bg-gradient-to-r from-gold to-gold-light bg-clip-text text-transparent">
            {t('buy.title')}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            {t('buy.howToBuy.description')}
          </p>
        </div>
      </section>

      {/* CEX Information */}
      <section className="section relative bg-bg-secondary/50">
        <div className="container">
          <div className="text-center mb-20">
            <div className="hero-badge inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('buy.exchange.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              {t('buy.exchange.title')}
            </h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('buy.exchange.description')}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Reku — Featured Local Exchange */}
            <div className="cta-section relative border-2 border-red-500/30 mb-10 overflow-hidden group">
              {/* Top accent bar */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-600 via-gold to-red-600" />

              <div className="text-center pt-4">
                {/* Badges */}
                <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-red-500/15 border border-red-500/30 rounded-full">
                    <span className="text-lg">🇮🇩</span>
                    <span className="text-red-400 font-semibold text-sm uppercase tracking-wider">
                      {t('buy.exchange.rekuBadge')}
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/15 border border-gold/30 rounded-full">
                    <i className="fas fa-star text-gold text-xs"></i>
                    <span className="text-gold font-semibold text-sm uppercase tracking-wider">
                      {t('buy.exchange.rekuRecommended')}
                    </span>
                  </div>
                </div>

                {/* Icon */}
                <div className="w-32 h-32 mx-auto mb-6 group-hover:scale-110 transition-transform rounded-2xl overflow-hidden">
                  <img src="/exchange/reku.webp" alt="Reku" className="w-full h-full object-cover" />
                </div>

                <h3 className="text-3xl font-black text-white mb-4 group-hover:text-gold transition-colors">
                  {t('buy.exchange.rekuTitle')}
                </h3>
                <p className="text-gray-300 mb-8 max-w-xl mx-auto leading-relaxed">
                  {t('buy.exchange.rekuDescription')}
                </p>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-4 mb-8 max-w-lg mx-auto">
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">{t('buy.exchange.rekuStats.pairLabel')}</div>
                    <div className="font-bold text-white">IDNG/IDR</div>
                  </div>
                  <div className="p-3 bg-gold/10 border border-gold/20 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">{t('buy.exchange.rekuStats.regulatedLabel')}</div>
                    <div className="font-bold text-gold">OJK</div>
                  </div>
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">{t('buy.exchange.rekuStats.tradingLabel')}</div>
                    <div className="font-bold text-white">24/7</div>
                  </div>
                </div>

                {/* CTA */}
                <a
                  href="https://reku.id/trade/IDNG-IDR"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-purple-700 via-purple-600 to-cyan-500 text-white font-black text-lg rounded-xl hover:shadow-xl hover:shadow-purple-500/25 transition-all transform hover:-translate-y-1"
                >
                  <i className="fas fa-chart-candlestick"></i>
                  <span>{t('buy.exchange.rekuBtn')}</span>
                  <i className="fas fa-external-link-alt text-sm"></i>
                </a>
              </div>
            </div>

            {/* International Exchanges */}
            <div className="mb-8">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-400 uppercase tracking-widest">
                  {t('buy.exchange.internationalTitle')}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="partner-card group text-center">
                  <div className="w-20 h-20 mx-auto mb-4 group-hover:scale-110 transition-transform rounded-xl overflow-hidden">
                    <img src="/exchange/bifinance.webp" alt="BiFinance" className="w-full h-full object-cover" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2 group-hover:text-gold transition-colors">BiFinance</h4>
                  <p className="text-gray-400 text-sm mb-4">IDNG/USDT</p>
                  <a
                    href="https://www.bifinance.com/trade?symbol=IDNG/USDT"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-emerald-400 text-white font-bold rounded-lg hover:shadow-lg hover:shadow-blue-500/20 transition-all transform hover:-translate-y-0.5 text-sm"
                  >
                    <span>{t('buy.exchange.bifinanceBtn')}</span>
                    <i className="fas fa-external-link-alt text-xs"></i>
                  </a>
                </div>

                <div className="partner-card group text-center">
                  <div className="w-20 h-20 mx-auto mb-4 group-hover:scale-110 transition-transform rounded-xl overflow-hidden">
                    <img src="/exchange/coinstore.webp" alt="Coinstore" className="w-full h-full object-cover" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">Coinstore</h4>
                  <p className="text-gray-400 text-sm mb-4">IDNG/USDT</p>
                  <a
                    href="https://www.coinstore.com/spot/IDNGUSDT"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-pink-500 to-fuchsia-600 text-white font-bold rounded-lg hover:shadow-lg hover:shadow-pink-500/20 transition-all transform hover:-translate-y-0.5 text-sm"
                  >
                    <span>{t('buy.exchange.coinstoreBtn')}</span>
                    <i className="fas fa-external-link-alt text-xs"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Safety Features */}
            <div className="cta-section relative border-2 border-gold/30">
              <div className="text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-shield-check text-5xl text-gold"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{t('buy.exchange.subtitle')}</h3>
                <p className="text-gray-300 mb-8">
                  {t('buy.exchange.description')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-left">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-check text-gold text-xl"></i>
                    </div>
                    <div>
                      <div className="text-white font-semibold mb-1">{t('buy.exchange.features.feature1')}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-check text-gold text-xl"></i>
                    </div>
                    <div>
                      <div className="text-white font-semibold mb-1">{t('buy.exchange.features.feature2')}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-check text-gold text-xl"></i>
                    </div>
                    <div>
                      <div className="text-white font-semibold mb-1">{t('buy.exchange.features.feature3')}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-check text-gold text-xl"></i>
                    </div>
                    <div>
                      <div className="text-white font-semibold mb-1">{t('buy.exchange.features.feature4')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Buying Guide */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="hero-badge inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('buy.exchange.howToBuyBadge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('buy.steps.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('buy.steps.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {buyingSteps.map((step, index) => (
              <div key={index} className="step-card modern-card text-center group">
                <div className="step-number mb-6 group-hover:scale-110 transition-transform">
                  {step.step}
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${step.icon} text-3xl text-gold`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                  {t(`buy.steps.step${step.step}.title`)}
                </h3>
                <p className="text-gray-400 leading-relaxed mb-4">
                  {t(`buy.steps.step${step.step}.description`)}
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <i className="fas fa-clock"></i>
                  <span>{step.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>




    </div>
  );
};